import React, { useRef, useCallback } from 'react'

import { Text } from 'components/typo'
import Layout from 'components/shared/Layout'
import GradientHeader from 'components/shared/GradientHeader'
import Image from 'components/shared/Image'
import Triangle from 'components/shared/Triangle'
import Person from 'components/shared/Person'
import LabelTitleText from 'components/shared/LabelTitleText'

import useLabelTitleTextAnimation from 'hooks/useLabelTitleTextAnimation'
import useResizeIndex from 'hooks/useResizeIndex'
import useAnimateInView from 'hooks/useAnimateInView'
import useComponentSize from '@rehooks/component-size'

import classnames from 'classnames/bind'
import css from './styles.module.scss'

const cx = classnames.bind(css)

const TextNextToImage = ({
  className,
  gradientHeader,
  decoration,
  content,
  quote,
  author,
  textPosition,
  image,
}) => {
  const componentRef = useRef()
  const elementRef = useRef()
  const textRef = useRef()
  const quoteRef = useRef()
  const authorRef = useRef()
  const imageRef = useRef()

  const { width, height } = useComponentSize(componentRef)
  const resizeIndex = useResizeIndex()

  const tl = useLabelTitleTextAnimation(
    {
      elementRef,
      components: [
        {
          key: 'text',
          ref: textRef,
          type: 'splitText',
        },
        {
          key: 'quote',
          ref: quoteRef,
          type: 'splitText',
        },
        {
          ref: imageRef,
          type: 'zoom',
        },
        {
          ref: authorRef,
          type: 'zoom',
        },
      ],
      linesClass: css.line,
    },
    [width, height, resizeIndex]
  )

  const [inViewRef] = useAnimateInView(
    {
      triggerOnce: true,
      ref: componentRef,
      animateIn: () => tl.restart(true),
    },
    [width, height, resizeIndex]
  )

  const setRef = useCallback((node) => {
    elementRef.current = node
    inViewRef(node)
  }, [])

  return (
    <div ref={componentRef}>
      <Layout type="maxWidth">
        <GradientHeader
          {...gradientHeader}
          wrapperType="mobileInnerWrapperLeftOnly"
          wrapperClassName={css.gradientHeaderWrapper}
          className={cx(className, css.TextNextToImage, {
            textOnTheRight: textPosition === 'right',
          })}
        >
          <div ref={setRef} className={css.contentWrapper}>
            <div className={css.textWrapper}>
              {content && (
                <LabelTitleText
                  className={css.text}
                  forwardRef={textRef}
                  titleProps={{ size: 'medium' }}
                  textProps={{ size: 'medium' }}
                  linkProps={{ size: 'big' }}
                  {...content}
                />
              )}
              {quote && (
                <Text
                  forwardRef={quoteRef}
                  size="smaller"
                  className={css.quote}
                >
                  {quote}
                </Text>
              )}
              {author && (
                <div ref={authorRef}>
                  <Person
                    className={css.author}
                    name={author.name}
                    description={author.description}
                    image={author.image}
                    companyLogo={author.companyLogo}
                  />
                </div>
              )}
            </div>
            {image && (
              <div ref={imageRef} className={css.imageWrapper}>
                <Image className={css.image} fit="contain" {...image} />
              </div>
            )}
          </div>
        </GradientHeader>
        {decoration && (
          <div className={cx(css.decoration, decoration)}>
            <Triangle className={css.triangle} />
            <Image
              src="/assets/decoration/dots.svg"
              media={{ width: 97, height: 30 }}
              className={css.dots}
              alt="dots"
            />
          </div>
        )}
      </Layout>
    </div>
  )
}

TextNextToImage.defaultProps = {
  textPosition: 'left',
}

export default TextNextToImage
